import { useState, Fragment, useEffect, useMemo } from 'react';
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import Pagination from "@mui/material/Pagination";
import SearchResultBlock from "../../components/Block";
import Stack from '@mui/material/Stack';
import DataGridComponent from "../../components/DataGridComponent/DataGridComponent";
import { GridColDef } from "@mui/x-data-grid";
import Button from '@mui/material/Button';
import DeleteIcon from '@mui/icons-material/Delete';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import IconButton from '@mui/material/IconButton';
import Checkbox from '@mui/material/Checkbox';
import Tooltip from '@mui/material/Tooltip';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import Switch from '@mui/material/Switch';
import FormControlLabel from '@mui/material/FormControlLabel';
import { getPerilsList, deletePerils } from '../address/data4homeAPI';

type Peril = {
  _id: string;
  type: string;
  geometry: {
    type: string;
    coordinates: number[];
  };
  properties: {
    place_id: string;
    address: string;
    startDate: string;
    endDate: string;
    url: string;
    source?: string;
    markedForDeletion: boolean;
  };
};

export default function Perils() {
    const [perils, setPerils] = useState<Peril[]>([]);
    const [perilCount, setPerilCount] = useState<number>(0);
    const [page, setPage] = useState<number>(1);
    const [selectedPerils, setSelectedPerils] = useState<string[]>([]);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [perilToDelete, setPerilToDelete] = useState<string | null>(null);
    const [showMarkedForDeletion, setShowMarkedForDeletion] = useState(false);
    const { enqueueSnackbar } = useSnackbar();
    const { t } = useTranslation();

    const itemsPerPage = 25;

    const fetchPerils = useMemo(() => {
        return (pageNumber: number) => {
            getPerilsList({ 
                page: pageNumber, 
                limit: itemsPerPage,
                markedForDeletion: showMarkedForDeletion 
            })
                .then(data => {
                    setPerils(data.data.map((peril: Peril) => {
                        const {_id: id, ...rest} = peril;
                        return ({
                            id,
                            ...rest,
                            properties: {
                                ...peril.properties,
                                startDate: new Date(peril.properties.startDate).toLocaleDateString(),
                                endDate: new Date(peril.properties.endDate).toLocaleDateString(),
                            }
                        })
                    }));
                    setPerilCount(data.pagination.total);
                })
                .catch((error) => {
                    enqueueSnackbar(
                        t('error.fetchingPerils'),
                        { variant: "error" }
                    );
                });
        }
    }, [enqueueSnackbar, t, showMarkedForDeletion]);

    useEffect(function() {
        fetchPerils(page);
    }, [fetchPerils, page]);

    const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
        setPage(value);
    };

    const handleMarkedForDeletionChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setShowMarkedForDeletion(event.target.checked);
        setPage(1); // Reset to first page when filter changes
    };

    const handleSelectionChange = (id: string) => {
        setSelectedPerils(prev => {
            if (prev.includes(id)) {
                return prev.filter(perilId => perilId !== id);
            } else {
                return [...prev, id];
            }
        });
    };

    const handleDeleteClick = (id?: string) => {
        setPerilToDelete(id || null);
        setDeleteDialogOpen(true);
    };

    const handleDeleteConfirm = async () => {
        const idsToDelete = perilToDelete ? [perilToDelete] : selectedPerils;
        
        try {
            await deletePerils(idsToDelete);
            
            enqueueSnackbar(
                t('perils.deleteSuccess'),
                { variant: "success" }
            );
            
            // Refresh the list
            fetchPerils(page);
            // Clear selection
            setSelectedPerils([]);
        } catch (error) {
            enqueueSnackbar(
                t('perils.deleteError'),
                { variant: "error" }
            );
        } finally {
            setDeleteDialogOpen(false);
            setPerilToDelete(null);
        }
    };

    const handleDeleteCancel = () => {
        setDeleteDialogOpen(false);
        setPerilToDelete(null);
    };

    const handleOpenSearch = (placeId: string) => {
        window.open(`/?placeId=${placeId}`, '_blank');
    };

    const columns: GridColDef[] = [
        {
            field: "selection",
            headerName: "",
            width: 50,
            sortable: false,
            renderCell: (params) => (
                <Checkbox
                    checked={selectedPerils.includes(params.row.id)}
                    onChange={() => handleSelectionChange(params.row.id)}
                />
            ),
        },
        {
            field: "properties.address",
            headerName: t("address"),
            flex: 2,
            minWidth: 200,
            sortable: false,
            valueGetter: (params) => params.row.properties.address,
        },
        {
            field: "properties.startDate",
            headerName: t("startDate"),
            flex: 1,
            minWidth: 120,
            sortable: false,
            valueGetter: (params) => params.row.properties.startDate,
        },
        {
            field: "properties.endDate",
            headerName: t("endDate"),
            flex: 1,
            minWidth: 120,
            sortable: false,
            valueGetter: (params) => params.row.properties.endDate,
        },
        {
            field: "properties.url",
            headerName: t("url"),
            flex: 1,
            minWidth: 120,
            sortable: false,
            renderCell: (params) => (
                <div>
                    {params.row.properties.url ? (
                        <a href={params.row.properties.url} target="_blank" rel="noreferrer">
                            {t("perils.viewSource")}
                        </a>
                    ) : (
                        "-"
                    )}
                </div>
            ),
        },
        {
            field: "properties.source",
            headerName: t("source"),
            flex: 1,
            minWidth: 120,
            sortable: false,
            valueGetter: (params) => params.row.properties.source,
        },
        {
            field: "search",
            headerName: "",
            width: 30,
            sortable: false,
            renderCell: (params) => (
                <Tooltip title={t('perils.openInSearch')}>
                    <span>
                        <IconButton
                            size="small"
                            onClick={() => handleOpenSearch(params.row.properties.place_id)}
                        >
                            <OpenInNewIcon fontSize="small" />
                        </IconButton>
                    </span>
                </Tooltip>
            ),
        },
        {
            field: "actions",
            headerName: "",
            width: 30,
            sortable: false,
            renderHeader: () => (
                !showMarkedForDeletion && (
                    <Tooltip title={t('perils.deleteSelected', { count: selectedPerils.length })}>
                        <span>
                            <IconButton
                                size="small"
                                onClick={() => handleDeleteClick()}
                                disabled={selectedPerils.length === 0}
                                color="error"
                            >
                                <DeleteIcon fontSize="small" />
                            </IconButton>
                        </span>
                    </Tooltip>
                )
            ),
            renderCell: (params) => (
                !showMarkedForDeletion && (
                    <Tooltip title={params.row.properties.markedForDeletion ? t('perils.alreadyMarkedForDeletion') : t('perils.delete')}>
                        <span>
                            <IconButton
                                size="small"
                                onClick={() => handleDeleteClick(params.row.id)}
                                disabled={params.row.properties.markedForDeletion}
                            >
                                <DeleteIcon fontSize="small" />
                            </IconButton>
                        </span>
                    </Tooltip>
                )
            ),
        },
    ];

    return (
        <Fragment>
            <SearchResultBlock title={t('perils.title')}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={showMarkedForDeletion}
                                onChange={handleMarkedForDeletionChange}
                            />
                        }
                        label={t('perils.showMarkedForDeletion')}
                    />
                    <Pagination
                        count={Math.ceil(perilCount / itemsPerPage)}
                        page={page}
                        onChange={handlePageChange}
                    />
                </Stack>
                <DataGridComponent
                    rows={perils}
                    columns={columns}
                    textNorows={t("noPerils")}
                />
                <Stack alignItems="center">
                    <Pagination
                        count={Math.ceil(perilCount / itemsPerPage)}
                        page={page}
                        onChange={handlePageChange}
                    />
                </Stack>
            </SearchResultBlock>

            <Dialog
                open={deleteDialogOpen}
                onClose={handleDeleteCancel}
            >
                <DialogTitle>
                    {perilToDelete ? t('perils.confirmDeleteTitle') : t('perils.confirmDeleteSelectedTitle')}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {perilToDelete 
                            ? t('perils.confirmDeleteMessage')
                            : t('perils.confirmDeleteSelectedMessage', { count: selectedPerils.length })
                        }
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleDeleteCancel}>
                        {t('cancel')}
                    </Button>
                    <Button onClick={handleDeleteConfirm} color="error" autoFocus>
                        {t('delete')}
                    </Button>
                </DialogActions>
            </Dialog>
        </Fragment>
    );
}
